const LOCAL_STUDY_KEY = 'pms-study-tracker';

const getIsoDate = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const getWeekStart = (isoDate, weekStart = 0) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = date.getDay();
  const offset = (weekday - weekStart + 7) % 7;
  date.setDate(date.getDate() - offset);
  return date.toISOString().slice(0, 10);
};

const countStudyDays = (studyDays) => {
  return Object.values(studyDays || {}).filter(minutes => Number(minutes) > 0).length;
};

const generateStudyBadges = (studyDayCount, totalPoints) => {
  const badges = [];
  for (let dayCount = 3; dayCount <= studyDayCount; dayCount += 3) {
    badges.push(`${dayCount}-Day Study Badge`);
  }
  const pointsPerBadge = 6; // 3 hours of study per badge (30 min = 1 point)
  for (let threshold = pointsPerBadge; threshold <= totalPoints; threshold += pointsPerBadge) {
    const hours = threshold / 2;
    badges.push(`${hours}-Hour Study Badge`);
  }
  return badges;
};

const computeCurrentStudyStreak = (studyDays, todayIso, weekStart = 0) => {
  let streak = 0;
  let missesThisWeek = 0;
  let currentWeekStart = getWeekStart(todayIso, weekStart);
  let current = new Date(todayIso);

  while (current.getTime() > 0) {
    const iso = getIsoDate(current);
    if (!iso) break;

    if (studyDays?.[iso]) {
      streak += 1;
    } else {
      if (missesThisWeek < 2) {
        missesThisWeek += 1;
      } else {
        break;
      }
    }

    current.setDate(current.getDate() - 1);
    const nextWeekStart = getWeekStart(current, weekStart);
    if (nextWeekStart !== currentWeekStart) {
      missesThisWeek = 0;
      currentWeekStart = nextWeekStart;
    }
  }

  return streak;
};

const computeSkipDaysRemaining = (studyDays, todayIso, weekStart = 0) => {
  const weekStartIso = getWeekStart(todayIso, weekStart);
  const start = new Date(weekStartIso);
  const today = new Date(todayIso);
  let misses = 0;
  let current = new Date(start);

  while (current <= today) {
    const iso = getIsoDate(current);
    if (!studyDays?.[iso]) {
      misses += 1;
    }
    current.setDate(current.getDate() + 1);
  }

  return Math.max(0, 2 - misses);
};

const normalizeStudyStats = (studyData = {}) => {
  const todayIso = getIsoDate(new Date());
  const studyDays = studyData.studyDays || {};
  const totalMinutes = Number(studyData.totalMinutes || 0);
  const totalPoints = Math.floor(totalMinutes / 30);
  const studyDayCount = countStudyDays(studyDays);
  const currentStreakDays = computeCurrentStudyStreak(studyDays, todayIso);
  const longestStreakDays = Math.max(Number(studyData.longestStreakDays || 0), currentStreakDays);

  return {
    totalMinutes,
    totalPoints,
    studyDays,
    studyDayCount,
    currentStreakDays,
    longestStreakDays,
    badges: generateStudyBadges(studyDayCount, totalPoints),
    lastStudyDate: studyData.lastStudyDate || null,
    skipDaysRemaining: computeSkipDaysRemaining(studyDays, todayIso)
  };
};

export const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

export const getLocalStudyStats = () => {
  if (!isLocalhost() || typeof window === 'undefined' || !window.localStorage) {
    return normalizeStudyStats();
  }

  try {
    const raw = window.localStorage.getItem(LOCAL_STUDY_KEY);
    if (!raw) return normalizeStudyStats();
    const parsed = JSON.parse(raw);
    return normalizeStudyStats(parsed);
  } catch (error) {
    console.error('Failed to load local study stats:', error);
    return normalizeStudyStats();
  }
};

export const saveLocalStudyStats = (studyData) => {
  if (!isLocalhost() || typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const normalized = normalizeStudyStats(studyData);
    window.localStorage.setItem(LOCAL_STUDY_KEY, JSON.stringify(normalized));
  } catch (error) {
    console.error('Failed to save local study stats:', error);
  }
};

export const addLocalStudyMinutes = (minutes, dateValue) => {
  const current = getLocalStudyStats();
  const isoDate = getIsoDate(dateValue || new Date());
  if (!isoDate) return current;

  const studyDays = {
    ...(current.studyDays || {}),
    [isoDate]: Number(current.studyDays?.[isoDate] || 0) + Number(minutes)
  };

  const nextData = {
    ...current,
    studyDays,
    totalMinutes: Number(current.totalMinutes || 0) + Number(minutes),
    lastStudyDate: isoDate
  };

  saveLocalStudyStats(nextData);
  return getLocalStudyStats();
};
